import React, {createContext, useContext, useState, useEffect, useCallback, useRef} from "react"
// API
import {APIGet, APIPut} from "@api";
// JWT
import { jwtDecode } from "jwt-decode";
// Translation
import {useTranslation} from "react-i18next";
// Loading
import Loading from "@/components/Loading/Loading";
import {useErrorBoundary} from "@/context/ErrorBoundaryProvider";
// Parent (dashboard) origin — derived at boot, multi-environment aware
import {isAllowedParentOrigin, parentOrigin} from "@/utils/parentOrigin";
// Unauthenticated-access guard
import {isAuthGuardEnabled, isWellFormedToken, redirectToLogin, TOKEN_TIMEOUT_MS} from "@/utils/authGuard";

const {REACT_APP_API_URL, REACT_APP_BILLING_V2} = process.env

const UserContext = createContext<any>(null)
export const useUser = () => useContext(UserContext)

export default function UserProvider({children}: any) {

    const {t} = useTranslation(["common"])
    const {handleError} = useErrorBoundary()

    const [loading, setLoading] = useState<boolean>(true)
    // token
    const [token, setToken] = useState<any>("")
    const [parsedToken, setParsedToken] = useState<any>(null)
    // data
    const [user, setUser] = useState<any>(null)
    const [fullOrganisation, setFullOrganisation] = useState<any>(null)
    const [organisation, setOrganisation] = useState<any>(null)
    const [orgMembers, setOrganisationMembers] = useState<any>(null)
    const [credits, setCredits] = useState<any>(null)
    const [gs1Credits, setGs1Credits] = useState<any>(null)
    const [userSettings, setUserSettings] = useState<any>(null)
    const [isSubOrganisation, setIsSubOrganisation] = useState<boolean>(false)

    const [canCreate, setCanCreate] = useState<boolean>(false)

    const userRoles = {
        admin: "ADMIN",
        owner: "OWNER",
        member: "MEMBER",
        analyst: "ANALYST",
        supervisor: "SUPERVISOR"
    }

    useEffect(() => {
        refreshToken()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (token !== "") {
            getUser()
            getOrganisation()
            getUserSettings()
            setParsedToken(jwtDecode(token))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    useEffect(() => {
        if (!!parsedToken) {
            // convert dates to unix timestamp format
            let unixStartDate = Date.now() / 1000
            let unixDuration = parsedToken.exp - unixStartDate
            // convert total unix time to vanilla
            let millisecondsDuration = unixDuration * 1000
            setTimeout(refreshToken, millisecondsDuration + 10)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedToken])

    useEffect(() => {
        if (organisation && organisation.organisation_id) getOrgMembers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisation])

    useEffect(() => {
        if (!!user && !!token && loading) setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, token])

    // Token management
    // Armed on every token request, cleared when a well-formed JWT arrives:
    // no answer from the dashboard means no session, so redirect to login.
    const tokenTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearTokenTimeout = () => {
        if (tokenTimeout.current) clearTimeout(tokenTimeout.current)
        tokenTimeout.current = null
    }

    const messageHandler = useCallback((event: any) => {
        // we only want messages from dashboard
        if (!isAllowedParentOrigin(event.origin)) return
        if (typeof event.data === 'string' && !event.data.includes("range")) {
            if (!isWellFormedToken(event.data)) return
            clearTokenTimeout()
            setToken(event.data)
        }
    }, [])

    useEffect(() => {
        window.addEventListener("message", messageHandler)
        return () => {
            window.removeEventListener("message", messageHandler)
            clearTokenTimeout()
        }
    }, [messageHandler])

    const refreshToken = () => {
        // Refresh message from dashboard — listener is registered separately via useEffect
        window.top?.postMessage("refreshToken", parentOrigin)
        if (isAuthGuardEnabled() && !tokenTimeout.current) {
            tokenTimeout.current = setTimeout(redirectToLogin, TOKEN_TIMEOUT_MS)
        }
    }

    const seeAnalyticsForProducts = (products: any) => {
        let payload = JSON.stringify(products)
        window.top?.postMessage(payload, parentOrigin)
        // Analyst "getting started" step — reaching analytics completes it.
        markStepDone("see_analytics")
    }

    // ── Getting-started steps tracker (dashboard game) ──
    // Flat map of the packaging steps marked done in /user/setting's
    // `steps_tracker` (missing payload = nothing done yet).
    const packagingStepsDone: Record<string, boolean> = React.useMemo(() => {
        const entry = (userSettings?.steps_tracker ?? []).find((s: any) => s.service_name === "packaging")
        const map: Record<string, boolean> = {}
        ;(entry?.steps ?? []).forEach((s: any) => { if (s?.step) map[s.step] = !!s.done })
        return map
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSettings])

    // Persist a completed step (idempotent — no-op when already done) and
    // reflect it locally right away so the tracker strikes through instantly.
    const markStepDone = (step: string) => {
        if (!token || packagingStepsDone[step]) return
        setUserSettings((prev: any) => {
            const tracker = [...(prev?.steps_tracker ?? [])]
            const i = tracker.findIndex((s: any) => s.service_name === "packaging")
            const steps = i >= 0 ? [...(tracker[i].steps ?? [])] : []
            const j = steps.findIndex((s: any) => s.step === step)
            if (j >= 0) steps[j] = {...steps[j], done: true}
            else steps.push({step, done: true})
            if (i >= 0) tracker[i] = {...tracker[i], steps}
            else tracker.push({service_name: "packaging", steps})
            return {...prev, steps_tracker: tracker}
        })
        APIPut(REACT_APP_API_URL + "/user/steps-tracker", {service_name: "packaging", step, done: true}, token).then()
    }

    const getUser = () => {
        if (!token) return
        APIGet(REACT_APP_API_URL + "/user", token).then((data) => {
            if (data.parsedBody) {
                setUser(data.parsedBody)
                // Trigger subsequent calls
                getOrganisation()
                getCredits()
            }
        }, () => handleError(t("error_get_user")))
    }

    const getFullOrganisation = () => {
        if (!token) return
        APIGet(REACT_APP_API_URL + "/orgs", token).then((data: any) => {
            if (data.parsedBody) { setFullOrganisation(data.parsedBody) }
        }, () => handleError(t("error_get_organisation")))
    }

    const getOrganisation = () => {
        if (!token) return
        APIGet(REACT_APP_API_URL + "/org", token).then((data: any) => {
            if (data.parsedBody) {
                setOrganisation(data.parsedBody)
                setIsSubOrganisation(!data.parsedBody.is_root_organisation)
                setCanCreate(data.parsedBody.can_user_consume_credits)
            }
        }, () => handleError(t("error_get_organisation")))
    }

    const getOrgMembers = () => {
        if (!token) return
        APIGet(REACT_APP_API_URL + "/org/"+ organisation.organisation_id + "/direct_users", token).then((data: any) => {
            setOrganisationMembers(data.parsedBody)
        }, () => handleError(t("error_get_org_members")))
    }

    const getCredits = () => {
        if (!token) return
        APIGet(REACT_APP_BILLING_V2 + "/credits", token).then((data: any) => {
            if (data.parsedBody) {
                const _credits = data.parsedBody.find((app: any) => app.service_name === "packaging")
                const _gs1 = data.parsedBody.find((app: any) => app.service_name === "packaging_gs1")
                setCredits(_credits)
                setGs1Credits(_gs1)
            }
        }, () => handleError(t("error_get_credits")))
    }

    const getUserSettings = () => {
        if (!token) return
        APIGet(REACT_APP_API_URL + "/user/setting", token).then((data: any) => {
            if (data.parsedBody) {
                setUserSettings(data.parsedBody)
            }
        }, () => handleError(t("error_get_settings")))
    }

    const savePackagingFilters = async (body: any) => {
        if (!token) return
        APIPut(REACT_APP_API_URL + "/user/qrfilters?filterType=packaging", body, token).then(() => {
        }).finally(() => getUserSettings())
    }

    if (loading) return <Loading type={"user-data"} />

    return (
        <UserContext.Provider
            value={{
                useUser,
                user, getUser,
                isSubOrganisation,
                organisation, getOrganisation,
                fullOrganisation, getFullOrganisation,
                orgMembers, getOrgMembers,
                credits, gs1Credits, getCredits,
                userRoles,
                token,
                canCreate,
                // User settings - mainly for filters
                userSettings, getUserSettings,
                // getting-started steps tracker
                packagingStepsDone, markStepDone,
                // message handlers
                seeAnalyticsForProducts,
                // filters
                savePackagingFilters
            }}
        >
            {children}
        </UserContext.Provider>
    )
}